import { useEffect, useMemo, type CSSProperties } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import {
  useEntitlements,
  type KnowledgeSection,
  type KnowledgeView,
} from "@cloudox/api-client";
import {
  ConfidenceBadge,
  EmptyState,
  LoadingState,
  MarkdownContent,
  SectionQualifiers,
  Skeleton,
  sectionSlug,
  splitMarkdownSections,
  stripLeadingH1,
  stripLeadingH2,
} from "@cloudox/ui";
import { DemoNotice } from "@/components/DemoNotice";
import { useKnowledgeContext } from "@/components/layout/KnowledgeShell";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useViewDocument } from "@/hooks/useKnowledgeData";
import { REFERENCE_VIEW_KEY, viewPath } from "@/lib/preview";

/**
 * The public preview's Knowledge View reader. One **section per page** to keep
 * each screen focused — the reader switches sections from the left rail (or the
 * prev/next controls), instead of scrolling one long document.
 */
export function ViewPage() {
  const { view, section } = useParams<{ view: string; section?: string }>();
  const { provider, nav } = useKnowledgeContext();
  const entitlements = useEntitlements();
  const viewMeta = nav.views.find((v) => v.key === view);
  const sectionMeta = viewMeta?.sections.find((s) => s.key === section);
  const unknownSection = Boolean(viewMeta && section && !sectionMeta);
  useDocumentTitle(
    !viewMeta
      ? "View not found"
      : unknownSection
        ? "Section not found"
        : sectionMeta
          ? `${sectionMeta.title} — ${viewMeta.displayName}`
          : `${viewMeta.displayName} view`,
  );

  if (!viewMeta) {
    return (
      <EmptyState
        headingLevel={1}
        title="View not found"
        description={`This environment has no "${view}" view.`}
        action={
          <Link to="/" className="btn btn-secondary">
            Back to the overview
          </Link>
        }
      />
    );
  }
  if (!entitlements.isViewEnabled(viewMeta.key)) {
    return (
      <EmptyState
        headingLevel={1}
        title={`${viewMeta.displayName} view is not in this plan`}
        description="The same interpreted knowledge powers every view."
      />
    );
  }

  // No section selected → canonicalise to the view's first section.
  if (!section && viewMeta.sections.length > 0) {
    return <Navigate to={viewPath(viewMeta.key, viewMeta.sections[0]!.key)} replace />;
  }

  // A section the manifest does not list is knowable before the document is
  // fetched, and refusing here is what keeps the heading honest: rendering the
  // view name as the h1 over a "not found" body tells the reader they are
  // somewhere that exists.
  if (unknownSection) {
    const first = viewMeta.sections[0];
    return (
      <EmptyState
        headingLevel={1}
        title="Section not found"
        description={`The ${viewMeta.displayName} view has no "${section}" section.`}
        {...(first
          ? {
              action: (
                <Link to={viewPath(viewMeta.key, first.key)} className="btn btn-secondary">
                  Go to {first.title}
                </Link>
              ),
            }
          : {})}
      />
    );
  }

  const isReference = viewMeta.key === REFERENCE_VIEW_KEY;

  return (
    <article>
      <header className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={viewPath(viewMeta.key)}
            className="text-[11px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: "var(--fg-subtle)", textDecoration: "none" }}
          >
            {viewMeta.displayName} {isReference ? "tables" : "View"}
          </Link>
          {/* A per-document confidence label is meaningless for the Reference
              Layer: its rows carry their own, which is exactly why the manifest
              reports Unknown for the document. Showing it here reads as a
              quality verdict on deterministic tables. */}
          {isReference ? null : (
            <ConfidenceBadge confidence={viewMeta.overallConfidence} />
          )}
        </div>
        {/* The section title is the page's real subject, so it — not the view
            name — is the h1. Before this the reader's first heading was an h2
            somewhere inside the prose. */}
        <h1
          className="mt-1.5 text-xl font-semibold sm:text-2xl"
          style={{ color: "var(--fg)" }}
        >
          {sectionMeta?.title ?? viewMeta.displayName}
        </h1>
        {viewMeta.audience ? (
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {viewMeta.audience}
          </p>
        ) : null}
        <DemoNotice variant="compact" className="mt-4" />
      </header>
      <SectionBody
        key={viewMeta.key}
        view={viewMeta}
        sectionKey={section}
        sectionMeta={sectionMeta}
        provider={provider}
      />
    </article>
  );
}

function SectionBody({
  view,
  sectionKey,
  sectionMeta,
  provider,
}: {
  readonly view: KnowledgeView;
  readonly sectionKey: string | undefined;
  readonly sectionMeta: KnowledgeSection | undefined;
  readonly provider: ReturnType<typeof useKnowledgeContext>["provider"];
}) {
  const doc = useViewDocument(provider, view.key);
  const { hash } = useLocation();
  const ready = doc.status === "ready";

  // Switching sections swaps the page content. Scroll to the targeted in-page
  // subsection anchor when one is requested, otherwise back to the top. Runs
  // once the document is ready so the heading element exists in the DOM.
  useEffect(() => {
    if (!ready) return;
    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [sectionKey, hash, ready]);

  const split = useMemo(
    () => (doc.status === "ready" ? splitMarkdownSections(doc.value.markdown) : null),
    [doc],
  );

  if (doc.status === "loading") {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  if (doc.status === "error") {
    return <EmptyState title="Narrative unavailable" description="This view's content could not be loaded." />;
  }
  if (doc.status !== "ready" || !split) return <LoadingState />;

  const imageBase = provider.viewAssetBaseUrl?.(view.key);

  // Map the navigation's ordered sections to the rendered document sections by
  // slug (falls back to positional alignment), so the rail keys and the page
  // content stay in lockstep regardless of minor slug differences.
  const bySlug = new Map(split.sections.map((s) => [s.slug, s] as const));
  const items = view.sections
    .map((ns, idx) => ({
      key: ns.key,
      title: ns.title,
      markdown: (bySlug.get(sectionSlug(ns.title)) ?? split.sections[idx])?.markdown,
    }))
    .filter((it): it is { key: string; title: string; markdown: string } => Boolean(it.markdown));

  // Fallback: no section structure (older single-file run) → render whole doc.
  if (items.length === 0) {
    return (
      <div className="doc-prose">
        <MarkdownContent markdown={stripLeadingH1(doc.value.markdown)} {...(imageBase ? { imageBase } : {})} />
      </div>
    );
  }

  const index = items.findIndex((it) => it.key === sectionKey);
  // Fail closed. This used to clamp a miss to 0, so a URL and a rail entry
  // naming one section rendered a different section's body with no indication
  // anything was wrong — the worst available outcome for a report whose value
  // is that its statements are attributable.
  if (index < 0) {
    return (
      <EmptyState
        title="Section not found"
        description={`The ${view.displayName} view has no "${sectionKey ?? ""}" section.`}
        action={
          <Link to={viewPath(view.key, items[0]!.key)} className="btn btn-secondary">
            Go to {items[0]!.title}
          </Link>
        }
      />
    );
  }
  const current = items[index]!;
  const prev = index > 0 ? items[index - 1] : undefined;
  const next = index < items.length - 1 ? items[index + 1] : undefined;

  return (
    <div>
      <SectionQualifiers
        source={sectionMeta?.source}
        confidence={sectionMeta?.confidence}
        needsImprovement={sectionMeta?.needsImprovement}
        kqiScore={sectionMeta?.kqiScore}
        viewConfidence={view.overallConfidence}
      />
      <div className="doc-prose">
        {/* The section's own `##` heading is the page's h1 (rendered in the
            header above), so it must not appear again at the top of the body. */}
        <MarkdownContent
          markdown={stripLeadingH2(current.markdown)}
          {...(imageBase ? { imageBase } : {})}
        />
      </div>
      <SectionPager view={view.key} prev={prev} next={next} />
    </div>
  );
}

function SectionPager({
  view,
  prev,
  next,
}: {
  readonly view: string;
  readonly prev?: { key: string; title: string };
  readonly next?: { key: string; title: string };
}) {
  if (!prev && !next) return null;
  return (
    <nav
      className="mt-10 flex items-stretch justify-between gap-3 border-t pt-5"
      style={{ borderColor: "var(--border)" }}
      aria-label="Section navigation"
    >
      {prev ? (
        <Link to={viewPath(view, prev.key)} className="pager-link" style={pagerStyle}>
          <span className="text-[11px]" style={{ color: "var(--fg-subtle)" }}>
            ← Previous
          </span>
          <span className="text-sm font-medium" style={{ color: "var(--fg)" }}>
            {prev.title}
          </span>
        </Link>
      ) : (
        <span />
      )}
      {next ? (
        <Link
          to={viewPath(view, next.key)}
          className="pager-link text-right"
          style={{ ...pagerStyle, alignItems: "flex-end" }}
        >
          <span className="text-[11px]" style={{ color: "var(--fg-subtle)" }}>
            Next →
          </span>
          <span className="text-sm font-medium" style={{ color: "var(--fg)" }}>
            {next.title}
          </span>
        </Link>
      ) : (
        <span />
      )}
    </nav>
  );
}

const pagerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.125rem",
  maxWidth: "48%",
  borderRadius: "0.5rem",
  border: "1px solid var(--border)",
  padding: "0.625rem 0.875rem",
  backgroundColor: "var(--bg-elevated)",
};
